import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';

import { BranchService } from 'src/app/services/branch/branch.service';
import { ModalFinishByPhoneComponent } from 'src/app/components/modals/modal-finish-by-phone/modal-finish-by-phone.component';
import {
  FinalLoanApplication,
  ApplicantOwnAnyVehicles,
} from 'src/app/services/loan/loan.model';
import { LoanService } from 'src/app/services/loan/loan.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { phoneNumberValidator } from 'src/app/directives/phone-number/phone-number.directive';
import { DataLayerService } from 'src/app/services/analytics/data-layer.service';
import { RouteName } from '../loan-routing.module';

@Component({
  selector: 'app-co-applicant-rent-vehicle',
  templateUrl: './co-applicant-rent-vehicle.component.html',
  styleUrls: ['./co-applicant-rent-vehicle.component.scss'],
})
export class CoApplicantRentVehicleComponent implements OnInit, OnDestroy {
  coApplicantRentFormGroup: FormGroup;
  loanApplication: FinalLoanApplication | null = null;
  vehiclesOwned: ApplicantOwnAnyVehicles | null = null;
  isDirtyAndMissingRequiredFields = false;

  private subscriptions: Array<Subscription> = [];

  constructor(
    private formBuilder: FormBuilder,
    private loanService: LoanService,
    private navService: NavigationService,
    private branchService: BranchService,
    private router: Router,
    private modalService: NgbModal,
    private titleService: Title,
    private dataLayerService: DataLayerService
  ) {
    this.titleService.setTitle(
      'Eagle Loan | Co-Applicant Mortgage/Rent and Vehicles'
    );
    this.navService.setBackNav('Co-Applicant Info', RouteName.coApplicant);
    this.branchService.setShowInfoButton(true);

    // Disabled FormControls will be hidden in the UI.
    this.coApplicantRentFormGroup = this.formBuilder.group({
      HousingContactName: [{ value: '', disabled: true }, Validators.required],
      HousingContactPhone: [
        { value: '', disabled: true },
        [Validators.required, phoneNumberValidator],
      ],
      MonthlyCost: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      MortgageBalance: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      Lienholder: [{ value: '', disabled: true }, Validators.required],
      MarketValue: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      ApplicantVehicles: this.formBuilder.array([]),
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.loanService.finalLoanApplication.subscribe(
        (data: FinalLoanApplication | null) => {
          if (data) {
            this.loanApplication = { ...data };
          }
        }
      )
    );

    this.subscriptions.push(
      this.coApplicantRentFormGroup.statusChanges.subscribe(
        (status: string) => {
          if (status && status === 'VALID') {
            this.isDirtyAndMissingRequiredFields = false;
          }
        }
      )
    );

    // On refresh of the page it will redirect to start of application.
    if (!this.loanApplication) {
      this.router.navigateByUrl(RouteName.default, { replaceUrl: true });
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  async onFinishByPhone(): Promise<void> {
    this.dataLayerService.logEvent(
      'button_clicked',
      'loan_application',
      'click',
      'Finish By Phone | Co-Applicant Mortgage/Rent & Vehicles'
    );

    const modalRef = this.modalService.open(ModalFinishByPhoneComponent);

    modalRef.componentInstance.loan = this.loanService.setApplicantRent(
      this.coApplicantRentFormGroup.value,
      true
    );

    const result = await modalRef.result;

    if (result && result.status === 'confirmed') {
      this.onConfirmed();
    }
  }

  onConfirmed() {
    this.loanService.clearApplication();
    this.router.navigateByUrl(RouteName.skippedConfirmation);
  }

  onNext() {
    this.isDirtyAndMissingRequiredFields = false;

    if (this.coApplicantRentFormGroup.invalid) {
      this.coApplicantRentFormGroup.markAllAsTouched();
      this.isDirtyAndMissingRequiredFields = true;
      return;
    }

    this.loanService.setApplicantRent(
      this.coApplicantRentFormGroup.value,
      true
    );

    this.router.navigateByUrl(RouteName.coApplicantEmployer);
  }
}
